/**
 * Server-side auth for Stytch B2B sessions.
 *
 * B2B JWTs include organization claims directly — no need for
 * email-domain guessing or Supabase invited_emails lookups.
 *
 * Returns the same AuthResult interface as the B2C version,
 * so API routes can switch between them without code changes.
 */

import { NextRequest, NextResponse } from "next/server"
import { createRemoteJWKSet, jwtVerify } from "jose"
import { isAdminUser, INTERNAL_API_KEY_HEADER } from "./constants"
import type { AuthResult, AuthOptions } from "./stytch-server"
import { AuthError, safeCompare } from "./stytch-server"

const stytchB2BProjectId = process.env.STYTCH_B2B_PROJECT_ID || ""
const stytchB2BSecret = process.env.STYTCH_B2B_SECRET || ""
const isTestProject = stytchB2BProjectId.startsWith("project-test-")
export const stytchB2BBaseUrl = isTestProject ? "https://test.stytch.com" : "https://api.stytch.com"
const B2B_JWKS = createRemoteJWKSet(
    new URL(`${stytchB2BBaseUrl}/v1/b2b/sessions/jwks/${stytchB2BProjectId}`)
)

export const B2B_SESSION_COOKIE = "stytch_b2b_session_jwt"
export const stytchBasicAuth = Buffer.from(`${stytchB2BProjectId}:${stytchB2BSecret}`).toString("base64")

/**
 * Extract member email from B2B JWT claims.
 * Tries the member claim first, then session authentication factors.
 */
export function extractB2BEmail(payload: Record<string, unknown>): string | null {
    const memberClaim = payload["https://stytch.com/member"] as
        | { email_address?: string }
        | undefined

    if (memberClaim?.email_address) return memberClaim.email_address

    const sessionClaim = payload["https://stytch.com/session"] as
        | {
              authentication_factors?: Array<{
                  type: string
                  email_factor?: { email_address: string }
                  google_oauth_factor?: { email_address: string }
                  microsoft_oauth_factor?: { email_address: string }
              }>
          }
        | undefined

    if (sessionClaim?.authentication_factors) {
        for (const factor of sessionClaim.authentication_factors) {
            if (factor.email_factor?.email_address) return factor.email_factor.email_address
            if (factor.google_oauth_factor?.email_address) return factor.google_oauth_factor.email_address
            if (factor.microsoft_oauth_factor?.email_address) return factor.microsoft_oauth_factor.email_address
        }
    }

    return null
}

/**
 * Fetch member email from Stytch B2B API using verified member ID and org ID.
 * This is the only trusted fallback when the JWT doesn't include email claims.
 */
export async function fetchMemberEmail(memberId: string, orgId: string): Promise<string | null> {
    try {
        const res = await fetch(`${stytchB2BBaseUrl}/v1/b2b/organizations/${orgId}/member?member_id=${memberId}`, {
            headers: {
                Authorization: `Basic ${stytchBasicAuth}`,
            },
        })
        if (!res.ok) return null
        const data = await res.json()
        return data.member?.email_address || null
    } catch {
        return null
    }
}

/**
 * Extract organization info from B2B JWT claims.
 */
function extractB2BOrganization(payload: Record<string, unknown>): { orgId: string; slug: string } | null {
    const orgClaim = payload["https://stytch.com/organization"] as
        | { organization_id?: string; slug?: string }
        | undefined

    if (orgClaim?.organization_id) {
        return {
            orgId: orgClaim.organization_id,
            slug: orgClaim.slug || "",
        }
    }

    return null
}

/**
 * Authenticate a B2B request by reading the Stytch B2B session JWT cookie.
 * Organization is extracted directly from JWT claims — no Supabase lookup needed.
 *
 * Email is resolved from verified JWT claims first, then via a server-side
 * Stytch API call using the verified member ID. Never trusts client input.
 */
export async function authenticateB2BRequest(
    request: NextRequest,
    options: Pick<AuthOptions, "allowInternalKey"> = {}
): Promise<AuthResult> {
    const { allowInternalKey = false } = options

    // Check internal API key if allowed
    if (allowInternalKey) {
        const internalKey = request.headers.get(INTERNAL_API_KEY_HEADER)
        if (internalKey && process.env.INTERNAL_API_KEY && safeCompare(internalKey, process.env.INTERNAL_API_KEY)) {
            return {
                email: "internal-service@trycardinal.ai",
                domain: "trycardinal.ai",
                orgDomain: "trycardinal.ai",
                isAdmin: true,
                stytchUserId: "internal-service",
            }
        }
    }

    // Verify B2B JWT signature using Stytch JWKS
    const stytchJwt = request.cookies.get(B2B_SESSION_COOKIE)?.value
    if (!stytchJwt) {
        throw new AuthError("No B2B session found")
    }

    let payload: Record<string, unknown>
    try {
        const result = await jwtVerify(stytchJwt, B2B_JWKS)
        payload = result.payload as Record<string, unknown>
    } catch {
        throw new AuthError("Invalid or expired B2B session")
    }

    // Extract org directly from JWT
    const orgInfo = extractB2BOrganization(payload)
    if (!orgInfo) {
        throw new AuthError("No organization in session")
    }

    // Resolve email: try JWT claims first, fall back to server-side Stytch API lookup.
    // Never trust client-supplied email headers or query params.
    const memberId = payload.sub as string
    let email = extractB2BEmail(payload)
    if (!email && memberId && orgInfo.orgId) {
        email = await fetchMemberEmail(memberId, orgInfo.orgId)
    }
    if (!email) {
        throw new AuthError("Could not determine member email")
    }

    const parts = email.split("@")
    if (parts.length !== 2 || !parts[1]) {
        throw new AuthError("Invalid email format")
    }
    const domain = parts[1].toLowerCase()

    return {
        email,
        domain,
        orgDomain: orgInfo.slug || domain,
        isAdmin: isAdminUser(email),
        stytchUserId: memberId || email,
    }
}

/**
 * Authenticate a B2B request, returning NextResponse on failure.
 */
export async function authenticateB2BOrError(
    request: NextRequest,
    options: Pick<AuthOptions, "allowInternalKey"> = {}
): Promise<AuthResult | NextResponse> {
    try {
        return await authenticateB2BRequest(request, options)
    } catch (err) {
        if (err instanceof AuthError) {
            return NextResponse.json({ error: err.message }, { status: err.statusCode })
        }
        return NextResponse.json({ error: "Internal server error" }, { status: 500 })
    }
}
